import { zodResolver } from '@hookform/resolvers/zod'
import { CheckedAddress } from '@sparkdotfi/common-universal'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useConfig } from 'wagmi'

import { AccountAirdropSummary, CheckEligibilityFormSchema } from '../types'
import { CheckEligibilityViewProps } from '../views/CheckEligibilityView'
import { airdropBreakdownQueryOptions } from './airdropBreakdownQuery'
import { getAllocationsSummary } from './getAllocationsSummary'
import { useConnectModal } from '@/domain/hooks/wallet'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { useSpkTokenRepository } from '@/features/spk/common/useSpkTokenRepository'

export interface UseCheckEligibilityViewPropsParams {
  accountAirdropSummary: AccountAirdropSummary
  chainId: number
}

export function useCheckEligibilityViewProps({
  accountAirdropSummary,
  chainId,
}: UseCheckEligibilityViewPropsParams): CheckEligibilityViewProps {
  const { account: address } = useConnectedAddress({ throwOnNotConnected: false })
  const spk = useSpkTokenRepository({ chainId, account: address }).findOneBySymbol(TokenSymbol('SPK'))
  const { openConnectModal = () => {} } = useConnectModal()
  const wagmiConfig = useConfig()

  const [targetAddress, setTargetAddress] = useState<CheckedAddress | undefined>(undefined)

  const checkEligibilityForm = useForm<CheckEligibilityFormSchema>({
    resolver: zodResolver(CheckEligibilityFormSchema),
    defaultValues: {
      address: '',
    },
    mode: 'onChange',
  })

  const {
    data: targetAddressAirdropBreakdown,
    isFetching: isEligibilityFetching,
    error: eligibilityCheckError,
  } = useQuery({
    ...airdropBreakdownQueryOptions({ address: targetAddress, wagmiConfig, chainId }),
    select: getAllocationsSummary,
  })

  function resetAddress(): void {
    setTargetAddress(undefined)
    checkEligibilityForm.reset()
  }

  return (() => {
    if (accountAirdropSummary && address) {
      return {
        status: 'result',
        variant: 'connected-wallet',
        address,
        spk,
        totalAllocation: accountAirdropSummary.totalAllocation,
        airdrops: accountAirdropSummary.airdrops,
        openConnectWalletModal: openConnectModal,
        resetAddress,
      }
    }

    if (!targetAddressAirdropBreakdown) {
      return {
        status: 'form',
        form: checkEligibilityForm,
        isEligibilityFetching,
        eligiblityCheckEnabled: checkEligibilityForm.formState.isValid,
        eligibilityCheckError:
          eligibilityCheckError && checkEligibilityForm.formState.isValid ? 'Error fetching eligibility' : undefined,
        onCheck: () => {
          if (!checkEligibilityForm.formState.isValid) {
            return
          }

          const { address } = checkEligibilityForm.getValues()
          setTargetAddress(address)
        },
        onConnectWallet: openConnectModal,
      }
    }

    return {
      status: 'result',
      spk,
      address: targetAddress!,
      totalAllocation: targetAddressAirdropBreakdown.totalAllocation,
      variant: 'manual-input',
      resetAddress,
      openConnectWalletModal: openConnectModal,
      airdrops: targetAddressAirdropBreakdown.airdrops,
    }
  })()
}
